import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playDing } from "../lib/sounds";
import {
  MessageCircle, X, ArrowRight, Send, Plus, Sparkles, Users,
  UserCircle2, Smile, Star, Heart, Zap, Rocket, Cloud, Feather,
  Flame, Leaf, Anchor, Compass, Coffee, Music, Moon, Sun,
} from "lucide-react";
import NewChatDialog from "./NewChatDialog";
import { ICON_MAP as SHARED_ICON_MAP } from "../lib/dailyIcon";

/**
 * Floating chat bubble — always-on, bottom-right.
 * - Polls /chat/threads every 8s to track unread.
 * - Auto-pops open when a NEW unread message arrives (from a DM or group)
 *   and the user isn't already on /chat.
 * - Can be manually closed; will re-open when the next new message lands.
 * - Hidden on /chat (the full chat page handles its own UI), on /login, and
 *   for temp roles (candidate_temp / ex_employee_temp).
 */
const POLL_MS = 8000;

// v109.9.88 — deterministic glowing-letter palette. Every teammate gets
// a stable colour anchored to the first character of their name.
const LETTER_GLOWS = [
  { text: "text-indigo-300",  glow: "rgba(129,140,248,0.85)"  },
  { text: "text-emerald-300", glow: "rgba(52,211,153,0.85)"   },
  { text: "text-rose-300",    glow: "rgba(251,113,133,0.85)"  },
  { text: "text-amber-300",   glow: "rgba(251,191,36,0.85)"   },
  { text: "text-violet-300",  glow: "rgba(196,181,253,0.85)"  },
  { text: "text-sky-300",     glow: "rgba(125,211,252,0.85)"  },
  { text: "text-lime-300",    glow: "rgba(190,242,100,0.85)"  },
  { text: "text-fuchsia-300", glow: "rgba(240,171,252,0.85)"  },
];
function letterGlow(seed) {
  const s = String(seed || "?").toUpperCase().charCodeAt(0) || 65;
  return LETTER_GLOWS[(s - 65 + LETTER_GLOWS.length) % LETTER_GLOWS.length];
}

// v109.9.90 — Deterministic per-name icon (Group-style logo for DMs).
// The chat thread list now shows a distinct lucide icon per teammate,
// seeded by the sum of char codes of their display name.
const THREAD_ICONS = [
  UserCircle2, Smile, Star, Heart, Zap, Rocket, Cloud, Feather,
  Flame, Leaf, Anchor, Compass, Coffee, Music, Moon, Sun,
];
function threadIcon(seed) {
  const raw = String(seed || "?");
  let sum = 0;
  for (let i = 0; i < raw.length; i += 1) sum += raw.charCodeAt(i);
  return THREAD_ICONS[sum % THREAD_ICONS.length];
}

// v109.9.91 — Given a DM thread + the daily-icons map, resolve the
// counter-party's picked lucide icon (falling back to a deterministic one
// derived from the thread title).
function dmIconFor(thread, currentUserId, dailyIcons, title) {
  if (thread && Array.isArray(thread.member_ids)) {
    const other = thread.member_ids.find((id) => String(id) !== String(currentUserId));
    const chosen = other ? dailyIcons?.[other] : null;
    if (chosen && SHARED_ICON_MAP[chosen]) return SHARED_ICON_MAP[chosen];
  }
  return threadIcon(title);
}
function GlowLetter({ name, size = 22, className = "", boost = false }) {
  const g = letterGlow(name);
  const letter = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className={`inline-flex items-center justify-center font-extrabold heading select-none ${g.text} ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.92,
        lineHeight: 1,
        textShadow:
          `0 0 5px ${g.glow},` +
          ` 0 0 12px ${g.glow},` +
          ` 0 0 22px ${g.glow}` +
          (boost ? `, 0 0 34px ${g.glow}` : ""),
      }}
      aria-hidden="true"
      data-keep-color
    >
      {letter}
    </span>
  );
}

const fmtRel = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch { return ""; }
};

export default function FloatingChatBubble() {
  const { user } = useAuth();
  const location = useLocation();
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [active, setActive] = useState(null); // active thread id while inside the popover
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [dailyIcons, setDailyIcons] = useState({});
  const lastUnreadKey = useRef("");
  const scrollRef = useRef(null);

  const isLoggedIn = !!user?.id;
  const isTemp = user?.role === "candidate_temp" || user?.role === "ex_employee_temp";
  const onChatPage = location.pathname.startsWith("/chat");
  const onLogin = location.pathname.startsWith("/login");
  const shouldRender = isLoggedIn && !isTemp && !onChatPage && !onLogin;

  // ─── Polling ──────────────────────────────────────────────────────────────
  const loadThreads = async () => {
    const [tRes, uRes, iRes] = await Promise.allSettled([
      api.get("/chat/threads"),
      api.get("/chat/unread-count"),
      api.get("/daily-icons/today"),
    ]);
    if (tRes.status === "fulfilled") setThreads(tRes.value.data || []);
    if (uRes.status === "fulfilled" && typeof uRes.value.data?.unread === "number") {
      setUnreadMsgCount(uRes.value.data.unread);
    }
    if (iRes.status === "fulfilled") setDailyIcons(iRes.value.data?.icons || {});
  };

  useEffect(() => {
    if (!shouldRender) return undefined;
    loadThreads();
    const t = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(t);
  }, [shouldRender]);

  // Detect *new* unread (a thread that wasn't unread before but is now).
  useEffect(() => {
    const currentUnread = new Set(threads.filter((t) => t.unread).map((t) => t.id));
    const key = Array.from(currentUnread).sort().join("|");
    const fingerprint = threads
      .filter((t) => t.unread && t.last_message_at)
      .map((t) => `${t.id}:${t.last_message_at}`)
      .sort()
      .join("|");
    const newKey = `${key}::${fingerprint}`;
    const had = lastUnreadKey.current;
    if (had && had !== newKey) {
      // A change occurred — was something added that we didn't have before?
      const prevSet = new Set(had.split("::")[0].split("|"));
      const arrived = Array.from(currentUnread).some((id) => !prevSet.has(id));
      const advanced = newKey.split("::")[1] !== had.split("::")[1];
      if ((arrived || advanced) && !onChatPage) {
        // Auto-pop: open the bubble and ding
        setOpen(true);
        setManuallyClosed(false);
        try { playDing(); } catch { /* ignore */ }
      }
    }
    lastUnreadKey.current = newKey;
    setUnreadIds(currentUnread);
  }, [threads, onChatPage]);

  // Load messages when a thread is opened inside the bubble.
  useEffect(() => {
    if (!active) { setMessages([]); return undefined; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/chat/threads/${active}/messages`);
        if (cancelled) return;
        setMessages(data || []);
        // Mark as read on the server, then refresh the unread badge so the
        // bubble dot disappears as soon as the user opens the thread.
        api.post(`/chat/threads/${active}/read`)
          .then(() => loadThreads())
          .catch(() => {});
        // Optimistically clear unread flag locally so the auto-pop doesn't refire.
        setThreads((arr) => arr.map((t) => t.id === active ? { ...t, unread: false } : t));
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [active]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, active]);

  if (!shouldRender) return null;

  const totalUnreadThreads = unreadIds.size;
  const totalUnreadMessages = unreadMsgCount;

  const toggle = () => {
    if (open) { setOpen(false); setActive(null); setManuallyClosed(true); }
    else { setOpen(true); setManuallyClosed(false); }
  };

  const close = () => { setOpen(false); setActive(null); setManuallyClosed(true); };

  const send = async () => {
    const text = draft.trim();
    if (!text || !active || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${active}/messages`, { text });
      setDraft("");
      const { data } = await api.get(`/chat/threads/${active}/messages`);
      setMessages(data || []);
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  // ─── UI ───────────────────────────────────────────────────────────────────
  const activeThread = threads.find((t) => t.id === active);

  return (
    <>
      {/* Bubble launcher */}
      {!open && (
        <button
          onClick={toggle}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 via-blue-600 to-violet-700 text-white shadow-xl shadow-indigo-500/40 hover:shadow-2xl hover:shadow-indigo-500/60 hover:-translate-y-0.5 active:scale-95 transition-all grid place-items-center group"
          data-testid="floating-chat-bubble"
          aria-label="Open chat"
        >
          <MessageCircle size={22} strokeWidth={2.2} className="group-hover:rotate-[-8deg] transition-transform" />
          {totalUnreadMessages > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-600 text-white text-[10.5px] font-extrabold grid place-items-center ring-2 ring-white shadow-md"
              data-testid="floating-chat-unread"
              title={`${totalUnreadMessages} unread message${totalUnreadMessages === 1 ? "" : "s"} in ${totalUnreadThreads} thread${totalUnreadThreads === 1 ? "" : "s"}`}
            >
              {totalUnreadMessages > 99 ? "99+" : totalUnreadMessages}
            </span>
          )}
        </button>
      )}

      {/* Popover panel — v109.9.63 refreshed design */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-1.5rem)] h-[560px] max-h-[calc(100vh-2rem)] rounded-[22px] shadow-[0_20px_60px_-10px_rgba(79,70,229,0.45)] border border-white/40 dark:border-white/10 bg-white dark:bg-slate-950 flex flex-col overflow-hidden animate-in fade-in-50 slide-in-from-bottom-4 ring-1 ring-black/5 dark:ring-white/5"
          data-testid="floating-chat-panel"
        >
          {/* Header — morphed glass matching the app theme (no gradient) */}
          <div
            className="relative px-4 py-3.5 flex items-center gap-2 shrink-0 overflow-hidden text-slate-900 dark:text-white border-b border-white/40 dark:border-white/10"
            style={{
              background: "rgba(255,255,255,0.06)",
              backdropFilter: "blur(18px) saturate(160%)",
              WebkitBackdropFilter: "blur(18px) saturate(160%)",
            }}
            data-keep-color
          >
            {/* Glowing accent bar aligning with the app theme */}
            <span
              className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(232,121,249,0.85) 30%, rgba(129,140,248,0.85) 70%, transparent)",
                filter: "blur(1px)",
                boxShadow: "0 0 14px rgba(139,92,246,0.55)",
              }}
              data-keep-color
              aria-hidden="true"
            />
            {active && (
              <button onClick={() => setActive(null)} className="relative w-7 h-7 grid place-items-center rounded-full hover:bg-slate-900/10 dark:hover:bg-white/15 active:scale-90 transition-all" aria-label="Back" data-testid="floating-chat-back">
                <ArrowRight size={14} className="rotate-180" />
              </button>
            )}
            {active ? (
              (() => {
                const activeTitle = activeThread?.name || (activeThread?.member_names && Object.entries(activeThread.member_names).filter(([id]) => id !== user.id).map(([, n]) => n).join(", ")) || "?";
                const g = letterGlow(activeTitle);
                const DmIcon = dmIconFor(activeThread, user.id, dailyIcons, activeTitle);
                return (
                  <div
                    className={`relative w-10 h-10 rounded-2xl grid place-items-center shrink-0 overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.55)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]`}
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      backdropFilter: "blur(10px) saturate(150%)",
                      WebkitBackdropFilter: "blur(10px) saturate(150%)",
                    }}
                    data-keep-color
                  >
                    <span
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: `radial-gradient(circle at 30% 20%, ${g.glow}, transparent 65%)` }}
                      data-keep-color
                    />
                    {activeThread?.type === "group" ? (
                      <Users size={20} className={g.text.split(" ")[0]} data-keep-color />
                    ) : (
                      <DmIcon
                        size={20}
                        className={g.text.split(" ")[0]}
                        style={{ filter: `drop-shadow(0 0 8px ${g.glow})` }}
                        data-keep-color
                      />
                    )}
                  </div>
                );
              })()
            ) : (
              <div
                className="relative w-10 h-10 rounded-2xl grid place-items-center shrink-0 overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.55)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(10px) saturate(150%)",
                  WebkitBackdropFilter: "blur(10px) saturate(150%)",
                }}
                data-keep-color
              >
                <Sparkles size={14} strokeWidth={2.4} className="absolute -top-1 -right-1 text-amber-400 dark:text-yellow-200/90" />
                <MessageCircle size={18} strokeWidth={2.4} className="text-indigo-600 dark:text-indigo-300" />
              </div>
            )}
            <div className="relative flex-1 min-w-0">
              <div className="text-[14px] font-black leading-tight truncate tracking-tight">
                {active ? (activeThread?.name || (activeThread?.member_names && Object.entries(activeThread.member_names).filter(([id]) => id !== user.id).map(([, n]) => n).join(", ")) || "Chat") : "Messages"}
              </div>
              <div className="text-[10px] text-slate-500 dark:text-white/70 font-semibold uppercase tracking-[0.14em] mt-0.5">
                {active
                  ? (activeThread?.type === "group" ? "Group chat" : "Direct message")
                  : totalUnreadMessages > 0
                    ? `${totalUnreadMessages} unread · ${threads.length} thread${threads.length === 1 ? "" : "s"}`
                    : `${threads.length} thread${threads.length === 1 ? "" : "s"}`}
              </div>
            </div>
            {!active && (
              <button
                onClick={() => setNewChatOpen(true)}
                className="relative w-8 h-8 grid place-items-center rounded-full bg-slate-900/5 hover:bg-slate-900/10 dark:bg-white/15 dark:hover:bg-white/25 active:scale-90 transition-all ring-1 ring-slate-900/10 dark:ring-white/25"
                aria-label="New chat"
                title="Start a new chat or group"
                data-testid="floating-chat-new"
              >
                <Plus size={15} strokeWidth={2.6} />
              </button>
            )}
            <button onClick={close} className="relative w-8 h-8 grid place-items-center rounded-full bg-slate-900/5 hover:bg-slate-900/10 dark:bg-white/15 dark:hover:bg-white/25 active:scale-90 transition-all ring-1 ring-slate-900/10 dark:ring-white/25" aria-label="Close" data-testid="floating-chat-close">
                <X size={15} />
              </button>
          </div>

          {/* Body */}
          {!active ? (
            <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-950" data-testid="floating-chat-thread-list">
              {threads.length === 0 ? (
                <div className="text-center py-14 px-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-500/20 dark:to-violet-500/20 mx-auto mb-3 grid place-items-center ring-1 ring-indigo-200/60 dark:ring-indigo-500/30">
                    <MessageCircle size={24} className="text-indigo-500 dark:text-indigo-300" />
                  </div>
                  <div className="text-[13.5px] font-bold text-slate-700 dark:text-slate-200">Start a conversation</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Ping teammates or spin up a group.</div>
                  <button
                    onClick={() => setNewChatOpen(true)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11.5px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-lg shadow-indigo-500/30 transition-all"
                    data-testid="floating-chat-empty-new"
                  >
                    <Plus size={13} strokeWidth={2.6} /> New chat
                  </button>
                </div>
              ) : (
                <div className="p-2 space-y-1.5">
                  {threads.map((t) => {
                    const isUnread = !!t.unread;
                    const title = t.name || Object.entries(t.member_names || {}).filter(([id]) => id !== user.id).map(([, n]) => n).join(", ") || "Chat";
                    const g = letterGlow(title);
                    const DmIcon = dmIconFor(t, user.id, dailyIcons, title);
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActive(t.id)}
                        className={`w-full text-left px-2.5 py-2.5 rounded-xl flex items-start gap-3 transition-all border ${isUnread
                            ? "bg-indigo-50 dark:bg-indigo-500/15 border-indigo-100 dark:border-indigo-500/30 shadow-sm"
                            : "bg-white dark:bg-slate-900 border-transparent hover:border-slate-100 dark:hover:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-900/70"}`}
                        data-testid={`floating-chat-thread-${t.id}`}
                      >
                        <div className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center relative overflow-hidden ${
                          t.type === "group"
                            ? "bg-white/60 dark:bg-white/5 ring-1 ring-violet-200/70 dark:ring-violet-500/30"
                            : "bg-white/60 dark:bg-white/5 ring-1 ring-indigo-200/70 dark:ring-indigo-500/30"
                        }`}>
                          {t.type === "group" ? (
                            <Users
                              size={20}
                              className="text-violet-500 dark:text-violet-300"
                              style={{ filter: "drop-shadow(0 0 8px currentColor)" }}
                              data-keep-color
                            />
                          ) : (
                            <DmIcon
                              size={20}
                              className={g.text.split(" ")[0]}
                              style={{ filter: `drop-shadow(0 0 8px ${g.glow})` }}
                              data-keep-color
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1.5">
                            <div className={`truncate text-[13px] ${isUnread ? "font-extrabold text-slate-900 dark:text-white" : "font-semibold text-slate-700 dark:text-slate-200"}`}>{title}</div>
                            <div className={`shrink-0 text-[10px] mono ${isUnread ? "text-indigo-500 dark:text-indigo-300 font-bold" : "text-slate-400 dark:text-slate-500"}`}>{fmtRel(t.last_message_at)}</div>
                          </div>
                          {t.last_message_text && (
                            <div className={`text-[11.5px] mt-0.5 truncate ${isUnread ? "text-slate-700 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"}`}>
                              {t.last_message_by_name && <b className="text-slate-600 dark:text-slate-300">{t.last_message_by_name}: </b>}
                              {t.last_message_text}
                            </div>
                          )}
                        </div>
                        {isUnread && (
                          <span className="shrink-0 self-center min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[9.5px] font-extrabold ring-2 ring-white dark:ring-slate-900">
                            {t.unread_count && t.unread_count > 1 ? t.unread_count : "•"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,rgba(79,70,229,0.04),rgba(255,255,255,0))] dark:bg-[linear-gradient(180deg,rgba(79,70,229,0.08),rgba(2,6,23,0))] p-3 space-y-1" data-testid="floating-chat-messages">
                {messages.length === 0 ? (
                  <div className="text-center text-[11px] text-slate-500 dark:text-slate-400 italic py-10">Say hello 👋</div>
                ) : messages.map((m, idx) => {
                  const mine = m.sender_id === user.id;
                  const prev = messages[idx - 1];
                  const showSender = !mine && (!prev || prev.sender_id !== m.sender_id);
                  const groupedTop = prev && prev.sender_id === m.sender_id;
                  return (
                    <div key={m.id} className={`flex items-end gap-1.5 ${mine ? "justify-end" : "justify-start"} ${groupedTop ? "mt-0.5" : "mt-2"}`}>
                      {!mine && (
                        <span className="shrink-0" style={{ width: 22 }}>
                          {showSender && <GlowLetter name={m.sender_name} size={22} />}
                        </span>
                      )}
                      <div className={`max-w-[78%] px-3.5 py-2 text-[12.5px] leading-relaxed shadow-sm ${
                        mine
                          ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-2xl rounded-br-md"
                          : "bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-2xl rounded-bl-md"
                      }`}>
                        {showSender && (
                          <div className="text-[10px] font-bold text-indigo-600 dark:text-indigo-300 mb-0.5 tracking-wide">{m.sender_name}</div>
                        )}
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`text-[9.5px] mt-1 font-medium ${mine ? "text-indigo-100/90 text-right" : "text-slate-400 dark:text-slate-500"}`}>{fmtRel(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-2.5 py-2.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center gap-2 shrink-0">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Write a message…"
                  className="flex-1 h-10 px-4 text-[13px] rounded-full bg-slate-100 dark:bg-slate-900 border border-transparent focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                  data-testid="floating-chat-input"
                />
                <button
                  onClick={send}
                  disabled={!draft.trim() || sending}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white grid place-items-center hover:shadow-lg hover:shadow-indigo-500/40 disabled:opacity-40 disabled:hover:shadow-none active:scale-95 transition-all"
                  data-testid="floating-chat-send"
                  aria-label="Send"
                >
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {/* hide manuallyClosed state hint — used to avoid auto-popping while
          user explicitly closed the bubble. Kept in state for future tweaks. */}
      <span hidden>{String(manuallyClosed)}</span>

      {/* New chat / group dialog — opens directly from the floating bubble */}
      <NewChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        onCreated={(t) => {
          if (!t?.id) return;
          // Refresh + jump straight into the new thread inside the bubble.
          loadThreads();
          setActive(t.id);
          if (!open) { setOpen(true); setManuallyClosed(false); }
        }}
      />
    </>
  );
}
